import { createApi } from '@reduxjs/toolkit/query/react';
import { deviceAxiosQuery } from '../../utils/axios/device.axios';
import { IAiModels } from '../../interfaces/excecutive/IAiModels';
import { ITips } from '../../interfaces/excecutive/ITips';
import { PaginatedData } from '../../interfaces/models/models/paginated-response.model';
import { IPrompts, IPromtsV2 } from '../../interfaces/excecutive/IPrompts';
import { ISources } from '../../interfaces/excecutive/ISources';
import {
    IHigherRank,
    IHigherRankPayload,
} from '../../interfaces/excecutive/IHigherRank';
import { ITracking } from '../../interfaces/excecutive/ITracking';
import {
    IFiltersExecutive,
    IFiltersExecutiveCallsWithPagination,
    IFiltersExecutiveWithPagination,
} from '../../interfaces/excecutive/IFiltersExecutive';
import {
    IAverageRanking,
    IIndustryRanking,
} from '../../interfaces/excecutive/IIndustryRanking';
import { ICalls } from '../../interfaces/excecutive/ICalls';
import { ICallAnalytics } from '../../interfaces/excecutive/ICallAnalytics';
import { ICallDetails } from '../../interfaces/excecutive/ICallDetails';
import {
    IMostCallsResponse,
    IMostCallsParams,
    ITransferredReasonsResponse,
    IBookingFailureReasonsResponse,
} from '../../interfaces/excecutive/ISpaAnalytics';
import {
    IClaireDemoCallDetailsResponse,
    IClaireDemoCountsResponse,
    IClaireDemoDashboardParams,
    IClaireDemoDashboardResponse,
    IClaireDemoPreviewResponse,
} from '../../interfaces/excecutive/IClaireDemoDashboard';

export interface UsageHistoryRecord {
    _id: string;
    hotelSub: string;
    monthStart: string;
    monthEnd: string;
    callsCount: number;
    minutesCount: number;
    activeAgentsCount: number;
    callsLimit: number;
    minutesLimit: number;
    callsOverage: number;
    minutesOverage: number;
    resetAt: string | null;
    createdAt: string;
    updatedAt: string;
}

export interface ISpaCallAiSummary {
    summary?: string;
    callOutcome?: string;
    transferReason?: string;
    desiredAction?: string;
    bookingSuccessful?: boolean;
    frustrationDetected?: boolean;
    bookingFailureReason?: string;
    keyPoints?: string[];
    generatedAt?: string;
}

export interface ISpaCall {
    callId: string;
    status?: string;
    durationSec?: number;
    startedAt?: string;
    endedAt?: string;
    endReason?: string;
    fromNumber?: string;
    toNumber?: string;
    hotelSub?: string;
    aiSummary?: ISpaCallAiSummary;
    booking?: Record<string, any>;
    transcriptText?: string;
}

export const executiveApi = createApi({
    reducerPath: 'executive',
    baseQuery: deviceAxiosQuery(),
    tagTypes: ['calls', 'models', 'topics', 'chats', 'tips'],
    endpoints: (builder) => ({
        getAllCalls: builder.query<
            {
                data: {
                    calls: PaginatedData<ICalls>;
                    totalByStatus: { count: number; _id: string }[];
                };
            },
            IFiltersExecutiveCallsWithPagination
        >({
            query: (payload) => ({
                url: `/calls/all-calls`,
                method: 'GET',
                params: {
                    page: payload.number,
                    size: payload.size,
                    search: payload.search,
                    status: payload.finalStatus,
                    reason: payload.endedReason,
                    companyNumber: payload.companyNumber,
                    type: payload.type,
                    dateRangeMax: payload.transactionDateRange?.[0],
                    dateRangeMin: payload.transactionDateRange?.[1],
                    durationMin: 0,
                    durationMax: payload.duration,
                },
            }),
        }),
        getAllSearchValues: builder.query<
            {
                data: {
                    finalStatus: string[];
                    endedReason: string[];
                    companyNumber: string[];
                    type: string[];
                }[];
            },
            void
        >({
            query: () => ({
                url: `/calls/all-search-values`,
                method: 'GET',
            }),
        }),
        getCallAnalytics2: builder.query<{ data: ICallAnalytics[] }, string[]>({
            query: (date) => ({
                url: (date && date.length)
                    ? `/calls/all-calls/analytics?startDate=${date?.[0]}&endDate=${date?.[1]}`
                    : `/calls/all-calls/analytics`,
                method: 'GET',
            }),
        }),
		getCallAnalytics: builder.query<ICallAnalytics, string[]>({
            query: (date) => ({
                url: (date && date.length)
                    ? `/spa-voice-agent/kpis?hotelSub=e28524d4-60b1-70c5-0228-8f8824193e20&startDate=${date?.[0]}&endDate=${date?.[1]}`
                    : `/spa-voice-agent/kpis?hotelSub=e28524d4-60b1-70c5-0228-8f8824193e20`,
                method: 'GET',
            }),
        }),
        getClaireDashboard: builder.query<
            IClaireDemoDashboardResponse,
            IClaireDemoDashboardParams
        >({
            query: (params) => ({
                url: `/claire-demo/dashboard`,
                method: 'GET',
                params,
            }),
        }),
        getClaireCounts: builder.query<IClaireDemoCountsResponse, void>({
            query: () => ({
                url: `/claire-demo/counts`,
                method: 'GET',
            }),
        }),
        getClairePreview: builder.query<IClaireDemoPreviewResponse, void>({
            query: () => ({
                url: `/claire-demo/preview`,
                method: 'GET',
            }),
        }),
        getClaireCallById: builder.query<IClaireDemoCallDetailsResponse, string>({
            query: (callId) => ({
                url: `/claire-demo/calls/${callId}`,
                method: 'GET',
            }),
        }),
        getClaireKpis: builder.query<
            any,
            { view?: 'today' | '7day' | '30day' } | void
        >({
            query: (params) => ({
                url: `/spa-voice-agent/kpis`,
                method: 'GET',
                params: {
                    hotelSub: '921544e4-20c1-703b-c1d4-62f60c91cf49',
                    ...(params?.view ? { view: params.view } : { view: 'today' }),
                },
            }),
        }),
        getClaireBookingFailureReasons: builder.query<
            IBookingFailureReasonsResponse,
            {
                hotelSub: string;
                assistantId?: string;
                view?: 'today' | '7day' | '30day';
                startDate?: string;
                endDate?: string;
            }
        >({
            query: ({ hotelSub, assistantId, view, startDate, endDate }) => ({
                url: `/spa-voice-agent/analytics/booking-failure-reasons`,
                method: 'GET',
                params: {
                    hotelSub,
                    ...(assistantId ? { assistantId } : {}),
                    ...(startDate && endDate
                        ? { startDate, endDate }
                        : view
                          ? { view }
                          : {}),
                },
            }),
        }),
        refreshClaireCallFromVapi: builder.mutation<unknown, string>({
            query: (callId) => ({
                url: `/claire-demo/calls/${callId}/refresh-from-vapi`,
                method: 'POST',
            }),
        }),

		// 
        getCallAnalyticsByDate: builder.query<{ data: ICallAnalytics[] }, void>(
            {
                query: () => ({
                    url: `/calls/all-calls/analytics-by-date`,
                    method: 'GET',
                }),
            },
        ),
        getCallById: builder.query<{ data: ICallDetails }, string>({
            query: (id) => ({
                url: `/calls/all-calls/${id}`,
                method: 'GET',
            }),
        }),

        getAIModels: builder.query<{ data: IAiModels[] }, string[]>({
            query: (date) => ({
                url: date
                    ? `/ai-search-engine/user-enabled-ai-models?min=${date?.[0]}&max=${date?.[1]}`
                    : `/ai-search-engine/user-enabled-ai-models`,
                method: 'PUT',
            }),
        }),
        getPromptTracking: builder.query<ITracking, IFiltersExecutive>({
            query: ({ platform, competitor, date }) => ({
                url: `/leo/tracking?modelId=${platform}&type=${competitor}&date=${date}`,
                method: 'GET',
            }),
        }),
        getOtaTracking: builder.query<any, any>({
            query: ({ platform, date, isClient }) => ({
                url: `/leo/ota-tracking/${
                    platform || 'all'
                }/sources?date=${date}${
                    isClient ? `&isClient=${isClient}` : ''
                }`,
                method: 'GET',
            }),
        }),
        getHigherRank: builder.query<
            PaginatedData<IHigherRank>,
            IHigherRankPayload
        >({
            query: (payload) => ({
                url: `/leo/report/${payload.platform}/higher-rank-competitors?page=${payload.number}&size=${payload.size}&sort=brand%2Casc&type=${payload.type}&search=${payload.search}&date=${payload.date}`,
                method: 'GET',
            }),
        }),
        getCompetitorRank: builder.query<any, any>({
            query: (payload) => ({
                url: `/leo/report/${payload.platform}/higher-rank-competitors?type=${payload.type}&date=${payload.date}`,
                method: 'GET',
            }),
        }),
        getTips: builder.query<
            PaginatedData<ITips>,
            IFiltersExecutiveWithPagination
        >({
            query: (payload) => ({
                url: `/leo/report/${payload.platform}/tips?page=${payload.number}&size=${payload.size}&sort=title%2Cdesc&type=${payload.competitor}&tab=${payload?.type}&date=${payload.date}`,
                method: 'GET',
            }),
            providesTags: ['tips'],
        }),
        getSources: builder.query<
            PaginatedData<ISources>,
            IFiltersExecutiveWithPagination
        >({
            query: (payload) => ({
                url: `/leo/report/ChatGPT/sources?page=${payload.number}&size=${payload.size}&sort=totalCitations%2Cdesc`,
                method: 'GET',
            }),
        }),
        getPrompt: builder.query<
            PaginatedData<IPrompts>,
            IFiltersExecutiveWithPagination
        >({
            query: (payload) => ({
                url: `/leo/report/${payload.platform}/prompts?page=${payload.number}&size=${payload.size}&sort=rank%2Casc&type=${payload.competitor}&date=${payload.date}`,
                method: 'GET',
            }),
        }),
        getPromptV2: builder.query<
            PaginatedData<IPromtsV2>,
            IFiltersExecutiveWithPagination
        >({
            query: (payload) => ({
                url: `/leo/report/${payload.platform}/prompts/v2?page=${payload.number}&size=${payload.size}&sort=rank%2Casc&type=${payload.competitor}&date=${payload.date}`,
                method: 'GET',
            }),
        }),
        getIndustryRanking: builder.query<IIndustryRanking, any>({
            query: (payload) => ({
                url: `/leo/industry-rankings/${
                    payload?.platform || 'all'
                }?page=${payload.number}&size=${payload.size}&date=${
                    payload.date
                }`,
                method: 'GET',
            }),
        }),
        getAverageRanking: builder.query<{ data: IAverageRanking[] }, string>({
            query: (date) => ({
                url: `/leo/average-rankings?date=${date}`,
                method: 'GET',
            }),
        }),
        getCompetitorDetails: builder.query<any, any>({
            query: (payload) => ({
                url: `/leo/competitors/${payload?.id}/ranking/details?${
                    payload?.platform && payload?.platform !== 'all'
                        ? `platform=${payload.platform}`
                        : ''
                }${
                    payload?.date
                        ? `${
                              payload?.platform && payload?.platform !== 'all'
                                  ? '&'
                                  : ''
                          }date=${payload.date}`
                        : ''
                }`,
                method: 'GET',
            }),
        }),
        getCapacityMetrics: builder.query<
            {
                config: {
                    goLiveDate: string;
                    activeAgentsCount: number;
                    callsLimitPerAgent: number;
                    minutesLimitPerAgent: number;
                };
                currentUsage: {
                    calls: number;
                    minutes: number;
                    callsLimit: number;
                    minutesLimit: number;
                    callsPercentage: number;
                    minutesPercentage: number;
                    callsRemaining: number;
                    minutesRemaining: number;
                };
                monthStart: string;
                monthEnd: string;
            },
            { sub: string; assistantId: string }
        >({
            query: ({ sub, assistantId }) => ({
                url: `/spa-voice-agent/fair-usage/capacity/test`,
                method: 'GET',
                params: {
                    sub,
                    assistantId,
                },
            }),
        }),
        getUsageHistory: builder.query<
            UsageHistoryRecord[],
            { sub: string; limit?: number }
        >({
            query: ({ sub, limit = 50 }) => ({
                url: `/spa-voice-agent/usage/history/test`,
                method: 'GET',
                params: {
                    sub,
                    limit,
                },
            }),
        }),
        getMostCalls: builder.query<IMostCallsResponse, IMostCallsParams>({
            query: ({ hotelSub, view, date }) => ({
                url: `/spa-voice-agent/analytics/most-calls`,
                method: 'GET',
                params: {
                    hotelSub,
                    view,
                    ...(date && { date }),
                },
            }),
        }),
        getTransferredReasons: builder.query<
            ITransferredReasonsResponse,
            {
                hotelSub: string;
                assistantId?: string;
                view?: 'today' | '7day' | '30day';
                startDate?: string;
                endDate?: string;
            }
        >({
            query: ({ hotelSub, assistantId, view, startDate, endDate }) => ({
                url: `/spa-voice-agent/analytics/transferred-reasons`,
                method: 'GET',
                params: {
                    hotelSub,
                    ...(assistantId ? { assistantId } : {}),
                    ...(startDate && endDate
                        ? { startDate, endDate }
                        : view
                          ? { view }
                          : {}),
                },
            }),
        }),
        getBookingFailureReasons: builder.query<
            IBookingFailureReasonsResponse,
            { hotelSub: string }
        >({
            query: ({ hotelSub }) => ({
                url: `/spa-voice-agent/analytics/booking-failure-reasons`,
                method: 'GET',
                params: {
                    hotelSub,
                },
            }),
        }),
        getSpaCallsByHotel: builder.query<
            ISpaCall[],
            { hotelSub: string; limit?: number }
        >({
            query: ({ hotelSub, limit = 50 }) => ({
                url: `/spa-voice-agent/calls/by-hotel`,
                method: 'GET',
                params: {
                    hotelSub,
                    limit,
                },
            }),
        }),
        getSpaCallById: builder.query<ISpaCall, string>({
            query: (callId) => ({
                url: `/spa-voice-agent/calls/${callId}`,
                method: 'GET',
            }),
        }),
    }),
});

export const {
    useGetAllCallsQuery,
    useGetAllSearchValuesQuery,
    useGetCallAnalyticsQuery,
    useGetClaireDashboardQuery,
    useLazyGetClaireDashboardQuery,
    useGetClaireCountsQuery,
    useGetClairePreviewQuery,
    useGetClaireCallByIdQuery,
    useLazyGetClaireCallByIdQuery,
    useGetClaireKpisQuery,
    useGetClaireBookingFailureReasonsQuery,
    useRefreshClaireCallFromVapiMutation,
    useGetCallAnalyticsByDateQuery,
    useGetCallByIdQuery,

    useGetPromptTrackingQuery,
    useGetAIModelsQuery,
    useGetHigherRankQuery,
    useGetTipsQuery,
    useGetSourcesQuery,
    useGetPromptQuery,
    useGetPromptV2Query,
    useGetIndustryRankingQuery,
    useGetAverageRankingQuery,
    useGetOtaTrackingQuery,
    useGetCompetitorRankQuery,
    useGetCompetitorDetailsQuery,
    useGetCapacityMetricsQuery,
    useGetUsageHistoryQuery,
    useGetMostCallsQuery,
    useGetTransferredReasonsQuery,
    useGetBookingFailureReasonsQuery,
    useGetSpaCallsByHotelQuery,
    useGetSpaCallByIdQuery,
} = executiveApi;
